<template>
  <div class="neo4j-report p-1" :class="{ 'neo4j-report--canvas': isAllGraphs }">
    <div class="neo4j-report__header">
      <div class="neo4j-report__heading">
        <h5 class="mb-0">{{ questionLabel }}</h5>
        <p v-if="businessHint" class="neo4j-report__hint text-muted small mb-0">
          {{ businessHint }}
        </p>
      </div>
      <b-button-group v-if="hasDaysFilter" size="sm">
        <b-button
          v-for="r in ranges"
          :key="String(r.days)"
          :variant="activeDays === r.days ? 'primary' : 'outline-primary'"
          @click="applyRange(r.days)"
        >
          {{ r.label }}
        </b-button>
      </b-button-group>
      <b-button-group v-if="hasSpeedFilter" size="sm">
        <b-button
          v-for="r in speedRanges"
          :key="String(r.value)"
          :variant="activeMinSpeed === r.value ? 'primary' : 'outline-primary'"
          @click="applySpeed(r.value)"
        >
          {{ r.label }}
        </b-button>
      </b-button-group>
      <b-button-group v-if="hasWindowFilter" size="sm">
        <b-button
          v-for="r in windowRanges"
          :key="String(r.value)"
          :variant="activeWindowSeconds === r.value ? 'primary' : 'outline-primary'"
          @click="applyWindow(r.value)"
        >
          {{ r.label }}
        </b-button>
      </b-button-group>
    </div>

    <div v-if="hasTimeline" class="neo4j-report__timeline">
      <span class="neo4j-report__timeline-label">
        {{
          scrubTime == null
            ? $t("grid.widgets_section.neo_dash.report.timeline.show_all")
            : $t("grid.widgets_section.neo_dash.report.timeline.up_to", { date: formatDateTime(scrubTime) })
        }}
      </span>
      <input
        type="range"
        class="neo4j-report__timeline-slider"
        :min="timelineBounds.min"
        :max="timelineBounds.max"
        :value="scrubValue"
        @input="onScrub($event.target.value)"
      />
      <button
        v-if="scrubTime != null"
        type="button"
        class="neo4j-report__timeline-reset"
        @click="scrubTime = null"
      >
        {{ $t("grid.widgets_section.neo_dash.report.timeline.show_all") }}
      </button>
    </div>

    <div v-if="loading" class="neo4j-report__state">
      <b-spinner small /> <span class="ml-1">{{ $t("actions.loading") }}…</span>
    </div>
    <div v-else-if="error" class="neo4j-report__state text-danger">{{ error }}</div>

    <!-- One separate node graph per color (each in its own card). Bar / Pie
         modes instead show the color totals in a single chart. -->
    <template v-else-if="isAllGraphs">
      <div v-if="hasColorBreakdown" class="neo4j-report__chartmode">
        <b-button-group size="sm">
          <b-button
            v-for="m in chartModes"
            :key="m.value"
            :variant="chartMode === m.value ? 'primary' : 'outline-primary'"
            @click="chartMode = m.value"
          >
            {{ m.label }}
          </b-button>
        </b-button-group>
      </div>

      <div
        v-if="!hasColorBreakdown || chartMode === 'graph'"
        ref="graphWrap"
        class="neo4j-report__graphwrap"
      >
        <b-card v-if="hasGraphData" no-body class="neo4j-report__graphcard">
          <e-charts
            ref="mergedGraphChart"
            :key="`graph-${renderKey}`"
            :options="mergedGraphOption"
            autoresize
            class="neo4j-report__graphfill"
            @click="onGraphClick"
          />
        </b-card>
        <div v-else class="neo4j-report__state text-muted">
          {{ $t("grid.widgets_section.neo_dash.report.empty") }}
        </div>

        <!-- Plate detail popover: follows the clicked node. -->
        <div
          v-if="detail.show"
          ref="detailPopover"
          class="neo4j-report__popover"
          :style="popoverStyle"
        >
          <div class="neo4j-report__popover-head">
            <span class="font-weight-bold">
              {{ detailHeaderLabel }}: {{ detailHeaderValue }}
            </span>
            <button
              type="button"
              class="neo4j-report__popover-close"
              aria-label="Close"
              @click="closeDetail"
            >×</button>
          </div>

          <!-- Single reused card (same one cars_by_color already had) for
               Plate/Camera/Vehicle alike: photo-or-placeholder + field list. -->
          <div class="neo4j-report__popover-body">
            <div v-if="detail.info.flag_reason" class="neo4j-report__clone-alert">
              <template v-if="detail.info.flag_reason === 'speeding'">
                ⚠ {{ $t("grid.widgets_section.neo_dash.report.speeding_alert", { speed: detail.info.max_speed }) }}
              </template>
              <template v-else-if="detail.info.flag_reason === 'convoy'">
                ⚠ {{ $t("grid.widgets_section.neo_dash.report.convoy_alert") }}
              </template>
              <template v-else>
                ⚠ {{ $t("grid.widgets_section.neo_dash.report.clone_alert") }}
              </template>
            </div>

            <div v-if="detailSceneImage" class="neo4j-report__scene-img">
              <b-img :src="detailSceneImage" fluid block :alt="detailHeaderValue" loading="lazy" />
            </div>

            <div class="neo4j-report__plate-img">
              <b-img
                v-if="detailImage"
                :src="detailImage"
                fluid
                block
                :alt="detailHeaderValue"
                loading="lazy"
              />
              <div v-else class="neo4j-report__det-noimg text-muted small">
                {{ $t("grid.widgets_section.neo_dash.report.no_plate_image") }}
              </div>
            </div>

            <dl class="neo4j-report__fields">
              <div v-for="f in detailFields" :key="f.key" class="neo4j-report__field">
                <dt>{{ f.label }}</dt>
                <dd>
                  <span v-if="f.swatch" class="neo4j-report__swatch" :style="tagStyle(f.swatch)"></span>
                  {{ f.value }}
                </dd>
              </div>
            </dl>

            <!-- camera_flow extra: routes through this camera. -->
            <div v-if="detail.kind === 'camera' && (detail.flows || []).length" class="neo4j-report__flows">
              <div class="small text-muted mb-1">
                {{ $t("grid.widgets_section.neo_dash.report.top_routes") }}
              </div>
              <div v-for="(f, i) in detail.flows.slice(0, 5)" :key="i" class="neo4j-report__flow-row small">
                <b>{{ f.from }} → {{ f.to }}</b> — {{ fmt(f.weight) }}
                <div v-if="(f.samplePlates || []).length" class="text-muted">
                  {{ f.samplePlates.join(", ") }}
                </div>
              </div>
            </div>

            <div v-if="!hasAnyDetail" class="text-muted small">
              {{ $t("grid.widgets_section.neo_dash.report.no_detections") }}
            </div>
          </div>
        </div>
      </div>

      <e-charts
        v-else-if="hasColorBreakdown"
        :key="`totals-${chartMode}`"
        :options="colorTotalsOption(chartMode)"
        autoresize
        class="neo4j-report__canvas"
      />
    </template>

    <b-row v-else class="mt-1">
      <b-col
        v-for="(chart, index) in charts"
        :key="`${renderKey}-${index}`"
        cols="12"
        :md="charts.length > 1 ? 6 : 12"
      >
        <b-card :title="chartTitle(chart)" class="mb-1">
          <div v-if="chart.type === 'kpi'" class="neo4j-report__kpi">
            {{ fmt(chart.value) }}{{ chart.suffix || "" }}
          </div>

          <b-table
            v-else-if="chart.type === 'table'"
            :items="chart.rows"
            :fields="tableFields(chart)"
            striped
            small
            responsive
            show-empty
            :empty-text="$t('grid.widgets_section.neo_dash.report.empty')"
          />

          <e-charts
            v-else-if="chart.type === 'grouped_bar'"
            :options="groupedBarOption(chart)"
            autoresize
            class="neo4j-report__chart"
          />

          <template v-else>
            <div class="neo4j-report__chartmode" v-if="canToggleChart">
              <b-button-group size="sm">
                <b-button
                  v-for="m in barChartModes"
                  :key="m.value"
                  :variant="(chart.chartMode || 'bar') === m.value ? 'primary' : 'outline-primary'"
                  @click="$set(chart, 'chartMode', m.value)"
                >
                  {{ m.label }}
                </b-button>
              </b-button-group>
            </div>
            <e-charts :options="singleChartOption(chart)" autoresize class="neo4j-report__chart" />
            <div v-if="!chart.total" class="text-muted text-center">
              {{ $t("grid.widgets_section.neo_dash.report.empty") }}
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>

    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BButton, BButtonGroup, BTable, BSpinner, BImg } from "bootstrap-vue";
import axios from "@axios";
import { COLOR_MAP } from "./vehicleColors";
// vue-echarts used directly, same setup as LprAnalyticsDashboard (the @core
// wrappers are incompatible with this vue-echarts beta).
import ECharts from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/graph";
import "echarts/lib/chart/pie";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/grid";
import "echarts/lib/component/legend";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BButtonGroup,
    BTable,
    BSpinner,
    BImg,
    ECharts,
  },
  props: {
    question: {
      type: String,
      required: true,
    },
    days: {
      type: Number,
      default: 7,
    },
    colors: {
      type: Array,
      default: () => [],
    },
    cameras: {
      type: Array,
      default: () => [],
    },
    plate: {
      type: String,
      default: "",
    },
    minSpeed: {
      type: Number,
      default: 50,
    },
    windowSeconds: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      activeDays: this.days,
      activeMinSpeed: this.minSpeed,
      activeWindowSeconds: this.windowSeconds,
      loading: false,
      error: null,
      charts: [],
      renderKey: 0,
      scrubTime: null,
      chartMode: "graph",
      boundGraphChart: null,
      detectionsByPlate: {},
      graphInfoByPlate: {},
      detail: {
        show: false,
        kind: "plate",
        plate: null,
        cameraName: null,
        vehicleCaption: null,
        detections: [],
        info: {},
        flows: [],
        detectionsCount: null,
        x: 0,
        y: 0,
      },
    };
  },
  created() {
    this.nodePositions = {};
  },
  watch: {
    charts() {
      this.scrubTime = null;
      this.nodePositions = {};
      if (!this.isAllGraphs || this.chartMode !== "graph") return;
      this.$nextTick(this.bindGraphChartEvents);
    },
    chartMode(value) {
      if (value !== "graph") return;
      this.$nextTick(this.bindGraphChartEvents);
    },
  },
  beforeDestroy() {
    this.unbindGraphChartEvents();
    this.unbindDocClick();
  },
  computed: {
    questionLabel() {
      return this.$t(`grid.widgets_section.neo_dash.questions.${this.question}`);
    },
    // Same query/data as always — just a business-facing reading of it for
    // whoever is deciding ad/billboard pricing off this report.
    businessHint() {
      const withHint = [
        "cars_by_camera", "cars_by_day", "cars_by_hour", "vehicle_brands", "vehicle_types",
        "exposure_time", "repeat_rate", "new_vs_repeat_hour",
      ];
      if (!withHint.includes(this.question)) return null;
      return this.$t(`grid.widgets_section.neo_dash.report.business_hint.${this.question}`);
    },
    hasDaysFilter() {
      return this.question !== "plates_registered";
    },
    ranges() {
      return [
        { days: 3, label: "3d" },
        { days: 7, label: "7d" },
        { days: 30, label: "30d" },
        { days: 90, label: "90d" },
        { days: 0, label: this.$t("grid.widgets_section.neo_dash.dashboard.range_all") },
      ];
    },
    hasSpeedFilter() {
      return this.question === "over_speed";
    },
    speedRanges() {
      return [50, 80, 100, 120].map((v) => ({ value: v, label: `${v} km/h` }));
    },
    hasWindowFilter() {
      return this.question === "convoy";
    },
    windowRanges() {
      return [5, 10, 20, 30].map((v) => ({ value: v, label: `${v}s` }));
    },
    isAllGraphs() {
      return this.charts.length > 0 && this.charts.every((c) => c.type === "graph");
    },
    hasGraphData() {
      return this.charts.some((c) => (c.nodes || []).length > 0);
    },
    timelineBounds() {
      let min = null;
      let max = null;
      (this.charts || []).forEach((chart) => {
        (chart.relationships || []).forEach((r) => {
          if (!r.detected_at) return;
          const t = new Date(r.detected_at).getTime();
          if (Number.isNaN(t)) return;
          if (min === null || t < min) min = t;
          if (max === null || t > max) max = t;
        });
      });
      return min !== null && max !== null ? { min, max } : null;
    },
    hasTimeline() {
      return this.isAllGraphs && !!this.timelineBounds && this.timelineBounds.max > this.timelineBounds.min;
    },
    scrubValue() {
      if (!this.timelineBounds) return 0;
      return this.scrubTime != null ? this.scrubTime : this.timelineBounds.max;
    },
    detailHeaderLabel() {
      const key = { camera: "camera", vehicle: "vehicle" }[this.detail.kind] || "plate";
      return this.$t(`grid.widgets_section.neo_dash.report.${key}`);
    },
    detailHeaderValue() {
      if (this.detail.kind === "camera") return this.detail.cameraName;
      if (this.detail.kind === "vehicle") return this.detail.vehicleCaption;
      return this.detail.plate;
    },
    popoverStyle() {
      return {
        left: `${this.detail.x}px`,
        top: `${this.detail.y}px`,
      };
    },
    // One field list feeds the single reused popover card for all three node
    // kinds — each kind just contributes whatever fields make sense for it.
    detailFields() {
      const i = this.detail.info || {};
      const t = (key) => this.$t(`grid.widgets_section.neo_dash.report.${key}`);
      const colorName = (c) => this.$t(`grid.widgets_section.neo_dash.color_names.${this.normColor(c)}`);
      const fields = [];

      if (this.detail.kind === "camera") {
        fields.push({ key: "detections", label: t("detections"), value: this.fmt(i.detections_count) });
        return fields;
      }

      if (this.detail.kind === "vehicle") {
        if (i.vehicle_color) {
          fields.push({ key: "vcolor", label: t("vehicle_color"), value: colorName(i.vehicle_color), swatch: i.vehicle_color });
        }
        if (i.owner_plate) {
          fields.push({ key: "owner", label: t("plate"), value: i.owner_plate });
        }
        return fields;
      }

      // Plate.
      if ((i.vehicle_variants || []).length > 1) {
        fields.push({ key: "vehicles", label: t("vehicles"), value: i.vehicle_variants.join(" / ") });
      } else {
        if (i.vehicle_brand) fields.push({ key: "brand", label: t("brand"), value: i.vehicle_brand });
        if (i.vehicle_type) fields.push({ key: "type", label: t("type"), value: i.vehicle_type });
      }
      if (i.vehicle_color) {
        fields.push({ key: "vcolor", label: t("vehicle_color"), value: colorName(i.vehicle_color), swatch: i.vehicle_color });
      }
      if (i.plate_color) {
        fields.push({ key: "pcolor", label: t("plate_color"), value: colorName(i.plate_color), swatch: i.plate_color });
      }
      if (i.speed) fields.push({ key: "speed", label: t("speed"), value: i.speed });
      if (i.direction) fields.push({ key: "direction", label: t("direction"), value: i.direction });
      if (i.camera_name) fields.push({ key: "camera", label: t("camera"), value: i.camera_name });
      if (i.detected_at) fields.push({ key: "date", label: t("date"), value: this.formatDateTime(i.detected_at) });
      if (this.detail.detectionsCount != null) {
        fields.push({ key: "detections", label: t("detections"), value: this.detail.detectionsCount });
      }
      if ((i.convoy_partners || []).length) {
        const value = i.convoy_partners
          .map((p) => this.$t("grid.widgets_section.neo_dash.report.convoy_partner_detail", {
            plate: p.plate,
            count: p.count,
            cameras: p.cameraCount,
          }))
          .join(", ");
        fields.push({ key: "convoy", label: t("convoy_partners"), value });
      }
      return fields;
    },
    hasAnyDetail() {
      return this.detailFields.length > 0 || (this.detail.flows || []).length > 0;
    },
    detailImage() {
      if (this.detail.kind !== "plate") return null;
      const det = (this.detail.detections || [])[0] || {};
      return det.plate_image_url || (this.detail.info || {}).plate_image_url;
    },
    detailSceneImage() {
      if (this.detail.kind !== "plate") return null;
      const det = (this.detail.detections || [])[0] || {};
      return det.scene_image_url || (this.detail.info || {}).scene_image_url;
    },
    chartModes() {
      return [
        { value: "graph", label: this.$t("grid.widgets_section.neo_dash.report.chart_mode.graph") },
        { value: "bar", label: this.$t("grid.widgets_section.neo_dash.report.chart_mode.bar") },
        { value: "pie", label: this.$t("grid.widgets_section.neo_dash.report.chart_mode.pie") },
      ];
    },
    barChartModes() {
      return [
        { value: "bar", label: this.$t("grid.widgets_section.neo_dash.report.chart_mode.bar") },
        { value: "pie", label: this.$t("grid.widgets_section.neo_dash.report.chart_mode.pie") },
      ];
    },
    canToggleChart() {
      return !this.isAllGraphs;
    },
    // cars_by_color returns one chart per selected color (chart.color set);
    // the other graph questions (camera_flow, plate_clones, plate_route)
    // return a single generic chart, so the bar/pie "totals" toggle and the
    // per-color legend don't apply to them.
    hasColorBreakdown() {
      return this.charts.length > 0 && this.charts.every((c) => !!c.color);
    },
    mergedGraphOption() {
      return this.hasColorBreakdown ? this.colorMergedGraphOption : this.genericMergedGraphOption;
    },
    colorMergedGraphOption() {
      const categories = [
        { name: this.$t("grid.widgets_section.neo_dash.cameras"), itemStyle: { color: "#8E44AD" } },
        ...this.charts.map((c) => ({
          name: `${this.$t(`grid.widgets_section.neo_dash.color_names.${c.color}`)} (${this.fmt(c.total)})`,
          itemStyle: this.plateStyle(c.color),
        })),
      ];

      const nodesById = {};
      const links = [];

      this.charts.forEach((chart, chartIndex) => {
        const { visibleNodeIds, visibleRels } = this.timelineVisibility(chart);
        const pasosByTarget = {};
        (chart.relationships || []).forEach((r) => {
          const match = /^(\d+)/.exec(r.caption || "");
          pasosByTarget[r.to] = (pasosByTarget[r.to] || 0) + (match ? parseInt(match[1], 10) : 0);
        });

        (chart.nodes || []).forEach((n) => {
          if (nodesById[n.id]) return;
          const isCamera = n.type === "Camera";
          const visible = !this.hasTimeline || visibleNodeIds.has(n.id);
          const pinned = this.nodePositions[n.id];
          nodesById[n.id] = {
            id: n.id,
            name: n.caption,
            category: isCamera ? 0 : chartIndex + 1,
            itemStyle: { ...(isCamera ? { color: "#8E44AD" } : this.plateStyle(chart.color)), opacity: visible ? 1 : 0 },
            symbolSize: isCamera ? 56 : this.plateSize(pasosByTarget[n.id] || 0),
            label: { show: visible },
            silent: !visible,
            revealed: visible,
            nodeType: n.type,
            plate: isCamera ? null : n.caption,
            ...(pinned ? { x: pinned.x, y: pinned.y, fixed: true } : {}),
          };
        });

        (chart.relationships || []).forEach((r) => {
          const visible = !this.hasTimeline || visibleRels.has(r);
          links.push({
            source: r.from,
            target: r.to,
            value: r.caption,
            lineStyle: { opacity: visible ? 1 : 0 },
          });
        });
      });

      return {
        tooltip: {
          formatter: (p) => (p.dataType === "edge" ? p.data.value : this.nodeTooltipHtml(p.data)),
        },
        legend: [{ data: categories.map((c) => c.name), bottom: 0 }],
        series: [
          {
            type: "graph",
            layout: "force",
            roam: true,
            draggable: true,
            categories,
            label: { show: true, position: "right", fontSize: 11 },
            force: { repulsion: 260, edgeLength: 150, gravity: 0.1 },
            edgeSymbol: ["none", "arrow"],
            edgeSymbolSize: 8,
            edgeLabel: { show: false },
            lineStyle: { color: "#bbb", curveness: 0.05 },
            data: Object.values(nodesById),
            links,
          },
        ],
      };
    },
    // camera_flow / plate_clones / plate_route: a single chart mixing
    // Camera/Plate/Vehicle nodes, categorized by node type instead of color.
    genericMergedGraphOption() {
      const chart = this.charts[0] || { nodes: [], relationships: [] };
      const { visibleNodeIds, visibleRels } = this.timelineVisibility(chart);
      const nodesList = chart.nodes || [];
      const nd = (key) => this.$t(`grid.widgets_section.neo_dash.report.node_types.${key}`);

      // Ordered category definitions. A node's category (and therefore its
      // legend swatch) is picked by the FIRST matching def, so the swatch
      // color and the node's actual color can never disagree — a flagged
      // Plate (plate_clones) gets its own red category instead of silently
      // borrowing the plain blue "Plate" swatch it doesn't render with.
      const categoryDefs = [
        { color: "#8E44AD", label: nd("camera"), match: (n) => n.type === "Camera" },
        { color: "#E74C3C", label: nd("plate_clone"), match: (n) => n.type === "Plate" && n.flag === "clone" },
        { color: "#E67E22", label: nd("plate_speeding"), match: (n) => n.type === "Plate" && n.flag === "speeding" },
        { color: "#C2185B", label: nd("plate_convoy"), match: (n) => n.type === "Plate" && n.flag === "convoy" },
        { color: "#2E86DE", label: nd("plate"), match: (n) => n.type === "Plate" && !n.flag },
        { color: "#27AE60", label: nd("vehicle"), match: (n) => n.type === "Vehicle" },
      ];
      const presentDefs = categoryDefs.filter((def) => nodesList.some(def.match));
      const categories = presentDefs.map((def) => ({ name: def.label, itemStyle: { color: def.color } }));
      const findDefIndex = (n) => presentDefs.findIndex((def) => def.match(n));

      // Camera node size for flow graphs = total traffic touching it.
      const weightByNode = {};
      // Plate node size = "N pasos" parsed off the edges pointing at it (same
      // convention as the color graphs).
      const pasosByTarget = {};
      (chart.relationships || []).forEach((r) => {
        if (r.weight != null) {
          weightByNode[r.from] = (weightByNode[r.from] || 0) + r.weight;
          weightByNode[r.to] = (weightByNode[r.to] || 0) + r.weight;
        }
        // Only plain DETECTED edges encode "pasos" in their caption; sequence
        // and convoy edges set `kind` and use it for something else ("3 veces
        // juntos" would otherwise get misread as 3 more pasos on the target).
        if (r.kind) return;
        const match = /^(\d+)/.exec(r.caption || "");
        if (match) pasosByTarget[r.to] = (pasosByTarget[r.to] || 0) + parseInt(match[1], 10);
      });

      const nodes = nodesList.map((n) => {
        const catIdx = Math.max(findDefIndex(n), 0);
        const def = presentDefs[catIdx] || { color: "#95A5A6" };
        const visible = !this.hasTimeline || visibleNodeIds.has(n.id);
        const pinned = this.nodePositions[n.id];
        let size = 26;
        if (n.type === "Camera") {
          size = Math.round(Math.min(60, 22 + Math.sqrt(weightByNode[n.id] || n.weight || 0) * 3));
        } else if (n.type === "Plate") {
          size = this.plateSize(pasosByTarget[n.id] || 0);
        }
        return {
          id: n.id,
          name: n.caption,
          category: catIdx,
          itemStyle: { color: def.color, opacity: visible ? 1 : 0 },
          symbolSize: size,
          label: { show: visible },
          silent: !visible,
          revealed: visible,
          nodeType: n.type,
          plate: n.type === "Plate" ? n.caption : null,
          color: n.type === "Vehicle" ? n.color : null,
          ...(pinned ? { x: pinned.x, y: pinned.y, fixed: true } : {}),
        };
      });

      const links = (chart.relationships || []).map((r) => {
        const visible = !this.hasTimeline || visibleRels.has(r);
        const baseStyle =
          r.kind === "sequence"
            ? { type: "dashed", color: "#F39C12", curveness: 0.15, width: 2 }
            : r.kind === "convoy"
              ? { color: "#C2185B", curveness: 0.2, width: Math.min(6, 1 + (r.weight || 1)) }
              : { color: "#bbb", curveness: 0.05 };
        return {
          source: r.from,
          target: r.to,
          value: r.caption,
          lineStyle: { ...baseStyle, opacity: visible ? 1 : 0 },
        };
      });

      return {
        tooltip: {
          formatter: (p) => (p.dataType === "edge" ? p.data.value : this.nodeTooltipHtml(p.data)),
        },
        legend: [{ data: categories.map((c) => c.name), bottom: 0 }],
        series: [
          {
            type: "graph",
            layout: "force",
            roam: true,
            draggable: true,
            categories,
            label: { show: true, position: "right", fontSize: 11 },
            force: { repulsion: 260, edgeLength: 150, gravity: 0.1 },
            edgeSymbol: ["none", "arrow"],
            edgeSymbolSize: 8,
            edgeLabel: { show: false },
            lineStyle: { color: "#bbb", curveness: 0.05 },
            data: nodes,
            links,
          },
        ],
      };
    },
  },
  mounted() {
    this.fetch();
  },
  updated() {
    if (!this.isAllGraphs || this.chartMode !== "graph" || this.loading) return;
    this.bindGraphChartEvents();
  },
  methods: {
    /** Bigger plate node when it has more passes; clamped so it stays readable. */
    plateSize(pasos) {
      return Math.round(Math.min(46, 18 + Math.sqrt(pasos) * 2.2));
    },
    getMergedGraphChartInstance() {
      const component = this.$refs.mergedGraphChart;
      if (!component) return null;
      return component.chart || component;
    },
    bindGraphChartEvents() {
      const chart = this.getMergedGraphChartInstance();
      if (!chart || typeof chart.on !== "function") return;
      if (this.boundGraphChart === chart) return;
      this.unbindGraphChartEvents();
      this.boundGraphChart = chart;
      chart.on("mouseup", this.syncGraphNodePositions);
      chart.on("finished", this.captureNodePositions);
    },
    unbindGraphChartEvents() {
      if (!this.boundGraphChart) return;
      this.boundGraphChart.off("mouseup", this.syncGraphNodePositions);
      this.boundGraphChart.off("finished", this.captureNodePositions);
      this.boundGraphChart = null;
    },
    syncGraphNodePositions() {
      this.captureNodePositions();
    },
    captureNodePositions() {
      const chart = this.getMergedGraphChartInstance();
      if (!chart || typeof chart.getModel !== "function") return;
      const seriesModel = chart.getModel().getSeriesByIndex(0);
      const graph = seriesModel && typeof seriesModel.getGraph === "function" ? seriesModel.getGraph() : null;
      if (!graph || typeof graph.eachNode !== "function") return;
      graph.eachNode((node) => {
        const layout = node.getLayout();
        if (!layout) return;
        const x = Array.isArray(layout) ? layout[0] : layout.x;
        const y = Array.isArray(layout) ? layout[1] : layout.y;
        if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) return;
        this.nodePositions[node.id] = { x, y };
      });
    },
    fmt(n) {
      return (n ?? 0).toLocaleString();
    },
    plateStyle(color) {
      const hex = COLOR_MAP[color] || "#2E86DE";
      const style = { color: hex };
      if (hex === "#FFFFFF") {
        style.borderColor = "#BDBDBD";
        style.borderWidth = 1;
      }
      return style;
    },
    singleChartOption(chart) {
      const mode = chart.chartMode || "bar";
      if (mode === "pie") return this.pieOption(chart);
      return this.barOption(chart);
    },
    colorTotalsOption(mode) {
      const labels = this.charts.map((c) =>
        this.$t(`grid.widgets_section.neo_dash.color_names.${c.color}`)
      );
      const totals = this.charts.map((c) => c.total || 0);
      const colors = this.charts.map((c) => COLOR_MAP[c.color] || "#2E86DE");

      if (mode === "pie") {
        return {
          tooltip: { trigger: "item" },
          legend: { bottom: 0 },
          series: [
            {
              type: "pie",
              radius: ["35%", "70%"],
              label: { formatter: "{b}: {c}" },
              data: this.charts.map((c, i) => ({
                name: labels[i],
                value: totals[i],
                itemStyle: this.plateStyle(c.color),
              })),
            },
          ],
        };
      }
      return {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "4%", bottom: "3%", top: "10%", containLabel: true },
        xAxis: { type: "category", data: labels, axisLabel: { interval: 0, rotate: 0 } },
        yAxis: { type: "value" },
        series: [
          {
            name: this.$t("grid.widgets_section.neo_dash.report.detections"),
            type: "bar",
            barMaxWidth: 50,
            data: this.charts.map((c, i) => ({
              value: totals[i],
              itemStyle: this.plateStyle(c.color),
            })),
          },
        ],
      };
    },
    pieOption(chart) {
      const labels = chart.categories || [];
      const values = chart.values || [];
      return {
        tooltip: { trigger: "item" },
        legend: { bottom: 0 },
        series: [
          {
            type: "pie",
            radius: ["35%", "70%"],
            label: { formatter: "{b}: {c}" },
            data: labels.map((name, i) => ({ name, value: values[i] })),
          },
        ],
      };
    },
    onScrub(value) {
      this.captureNodePositions();
      this.scrubTime = Number(value);
    },
    timelineVisibility(chart) {
      const rels = chart.relationships || [];
      const timed = [];
      const untimed = [];
      rels.forEach((r) => (r.detected_at ? timed : untimed).push(r));

      const visibleTimed = timed.filter((r) => new Date(r.detected_at).getTime() <= this.scrubValue);
      const visibleNodeIds = new Set();
      visibleTimed.forEach((r) => {
        visibleNodeIds.add(r.from);
        visibleNodeIds.add(r.to);
      });

      const visibleUntimed = new Set();
      let added = true;
      while (added) {
        added = false;
        untimed.forEach((r) => {
          if (visibleUntimed.has(r)) return;
          const fromVisible = visibleNodeIds.has(r.from);
          const toVisible = visibleNodeIds.has(r.to);
          if (!fromVisible && !toVisible) return;
          visibleUntimed.add(r);
          if (!fromVisible) {
            visibleNodeIds.add(r.from);
            added = true;
          }
          if (!toVisible) {
            visibleNodeIds.add(r.to);
            added = true;
          }
        });
      }

      const visibleRels = new Set([...visibleTimed, ...visibleUntimed]);
      return { visibleNodeIds, visibleRels };
    },
    applyRange(days) {
      this.activeDays = days;
      this.fetch();
    },
    applySpeed(value) {
      this.activeMinSpeed = value;
      this.fetch();
    },
    applyWindow(value) {
      this.activeWindowSeconds = value;
      this.fetch();
    },
    chartTitle(chart) {
      if (chart.color) {
        const name = this.$t(`grid.widgets_section.neo_dash.color_names.${chart.color}`);
        return `${name} — ${this.fmt(chart.total)}`;
      }
      // KPI cards (repeat_rate report) each carry their own metric label
      // since several of them render side by side.
      if (chart.metric) {
        return this.$t(`grid.widgets_section.neo_dash.report.${chart.metric}`);
      }
      return null;
    },
    tableFields(chart) {
      const labelKeys = {
        plate: "plate",
        total: "detections",
        camera: "camera",
        avg_speed: "avg_speed",
        exposure_index: "exposure_index",
      };
      const formatters = {
        avg_speed: (v) => `${v} km/h`,
        total: (v) => this.fmt(v),
        exposure_index: (v) => this.fmt(v),
      };
      return (chart.columns || []).map((key) => ({
        key,
        label: this.$t(`grid.widgets_section.neo_dash.report.${labelKeys[key] || key}`),
        formatter: formatters[key],
      }));
    },
    barOption(chart) {
      const valueAxis = { type: "value" };
      const categoryAxis = { type: "category", data: chart.categories };
      const series = {
        name: this.$t("grid.widgets_section.neo_dash.report.detections"),
        type: "bar",
        barMaxWidth: 40,
        data: chart.values,
      };
      const color = chart.color ? COLOR_MAP[chart.color] : null;
      if (color) {
        series.itemStyle = { color };
        if (color === "#FFFFFF") {
          series.itemStyle.borderColor = "#BDBDBD";
          series.itemStyle.borderWidth = 1;
        }
      }
      return {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: "3%", right: "4%", bottom: "3%", top: "10%", containLabel: true },
        xAxis: chart.horizontal ? valueAxis : categoryAxis,
        yAxis: chart.horizontal ? categoryAxis : valueAxis,
        series: [series],
      };
    },
    // new_vs_repeat_hour: stacked "nuevas"/"recurrentes" bars per hour.
    groupedBarOption(chart) {
      const seriesColor = { new_plates: "#2E86DE", repeat_plates: "#27AE60" };
      const series = chart.series || [];
      return {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: {
          bottom: 0,
          data: series.map((s) => this.$t(`grid.widgets_section.neo_dash.report.${s.key}`)),
        },
        grid: { left: "3%", right: "4%", bottom: "14%", top: "10%", containLabel: true },
        xAxis: { type: "category", data: chart.categories },
        yAxis: { type: "value" },
        series: series.map((s) => ({
          name: this.$t(`grid.widgets_section.neo_dash.report.${s.key}`),
          type: "bar",
          stack: "total",
          barMaxWidth: 24,
          itemStyle: { color: seriesColor[s.key] },
          data: s.values,
        })),
      };
    },
    fetch() {
      this.loading = true;
      this.error = null;

      const params = { question: this.question };
      if (this.hasDaysFilter && this.activeDays > 0) {
        params.days = this.activeDays;
      }
      if (this.question === "cars_by_color" && this.colors.length) {
        params.colors = this.colors;
      }
      if (this.cameras.length) {
        params.cameras = this.cameras;
      }
      if (this.question === "plate_route" && this.plate) {
        params.plate = this.plate;
      }
      if (this.question === "over_speed") {
        params.min_speed = this.activeMinSpeed;
      }
      if (this.question === "convoy") {
        params.window_seconds = this.activeWindowSeconds;
      }

      axios
        .get("/v1/detection_event/graph-report", { params })
        .then(({ data }) => {
          const payload = (data && data.data) || {};
          this.charts = payload.charts || [];
          this.buildDetectionsByPlate(payload);
          this.renderKey += 1;
        })
        .catch((e) => {
          this.error =
            e?.response?.data?.message ||
            this.$t("grid.widgets_section.neo_dash.report.error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    buildDetectionsByPlate(payload) {
      const map = {};
      const push = (det) => {
        if (!det || !det.plate) return;
        const key = String(det.plate).toUpperCase();
        (map[key] = map[key] || []).push(det);
      };
      (payload.detections || []).forEach(push);
      (this.charts || []).forEach((chart) => {
        (chart.detections || []).forEach(push);
      });
      // Most recent sighting first when available.
      Object.values(map).forEach((list) => {
        list.sort((a, b) => {
          const ta = a.detected_at ? new Date(a.detected_at).getTime() : 0;
          const tb = b.detected_at ? new Date(b.detected_at).getTime() : 0;
          return tb - ta;
        });
      });
      this.detectionsByPlate = map;
      this.graphInfoByPlate = this.buildGraphInfoByPlate();
    },

    buildGraphInfoByPlate() {
      const info = {};
      const cameras = {};
      const vehicles = {};
      const flaggedPlates = {};
      const maxSpeedByPlate = {};
      (this.charts || []).forEach((chart) => {
        (chart.nodes || []).forEach((n) => {
          if (n.type === "Camera") cameras[n.id] = n.caption;
          if (n.type === "Vehicle") vehicles[n.id] = n.caption;
          if (n.type === "Plate" && n.flag) flaggedPlates[String(n.caption).toUpperCase()] = n.flag;
          if (n.type === "Plate" && n.max_speed != null) {
            maxSpeedByPlate[String(n.caption).toUpperCase()] = n.max_speed;
          }
        });
        (chart.relationships || []).forEach((r) => {
          const to = String(r.to || "");
          const plateId = to.startsWith("plate:") ? to.slice(6) : null;
          if (!plateId) return;
          const key = plateId.toUpperCase();
          const cur = info[key] || {};
          // Camera -> Plate (DETECTED edge carries sighting detail).
          if (r.from && String(r.from).startsWith("cam:")) {
            const camId = String(r.from).slice(4);
            if (!cur.camera_name && cameras[r.from]) cur.camera_name = cameras[r.from];
            if (!cur.camera_id) cur.camera_id = camId;
            // "3 pasos" -> 3 detections count
            const m = /^(\d+)/.exec(r.caption || "");
            if (m) {
              cur.detections_count = (cur.detections_count || 0) + parseInt(m[1], 10);
            }
            [
              "speed", "direction", "plate_color", "vehicle_type",
              "vehicle_brand", "vehicle_color", "detected_at",
              "plate_image_url", "plate_image_path",
              "scene_image_url", "scene_image_path",
            ].forEach((f) => {
              if (r[f] != null && cur[f] == null) cur[f] = r[f];
            });
          }
          info[key] = cur;
        });
        // Plate -> Vehicle (ON_VEHICLE edge): fill in vehicle name parsed
        // from the Vehicle node caption ("Toyota Sedan").
        (chart.relationships || []).forEach((r) => {
          const from = String(r.from || "");
          const plateId = from.startsWith("plate:") ? from.slice(6) : null;
          if (!plateId) return;
          const key = plateId.toUpperCase();
          const v = vehicles[r.to];
          if (!v) return;
          const cur = info[key] || {};
          cur.vehicle_variants = cur.vehicle_variants || [];
          if (!cur.vehicle_variants.includes(v)) cur.vehicle_variants.push(v);
          if (!cur.vehicle_brand || !cur.vehicle_type) {
            const parts = String(v).split(/\s+/);
            if (!cur.vehicle_brand) cur.vehicle_brand = parts[0];
            if (!cur.vehicle_type && parts.length > 1) cur.vehicle_type = parts.slice(1).join(" ");
          }
          info[key] = cur;
        });
        // Convoy edges (Plate<->Plate): who this plate keeps appearing with.
        (chart.relationships || []).forEach((r) => {
          if (r.kind !== "convoy") return;
          const fromKey = String(r.from || "").startsWith("plate:") ? String(r.from).slice(6).toUpperCase() : null;
          const toKey = String(r.to || "").startsWith("plate:") ? String(r.to).slice(6).toUpperCase() : null;
          if (!fromKey || !toKey) return;
          const addPartner = (key, partnerPlate) => {
            const cur = info[key] || {};
            cur.convoy_partners = cur.convoy_partners || [];
            cur.convoy_partners.push({ plate: partnerPlate, count: r.weight, cameraCount: r.camera_count });
            info[key] = cur;
          };
          addPartner(fromKey, toKey);
          addPartner(toKey, fromKey);
        });
      });
      Object.keys(flaggedPlates).forEach((key) => {
        info[key] = info[key] || {};
        info[key].flag_reason = flaggedPlates[key];
        if (maxSpeedByPlate[key] != null) info[key].max_speed = maxSpeedByPlate[key];
      });
      return info;
    },
    onGraphClick(params) {
      if (!params || params.dataType !== "node") return;
      const node = params.data || {};
      if (node.revealed === false) return;
      if (node.nodeType === "Camera") {
        this.showCameraDetail(node, params);
      } else if (node.nodeType === "Plate") {
        this.showPlateDetail(node, params);
      } else if (node.nodeType === "Vehicle") {
        this.showVehicleDetail(node, params);
      }
    },
    showPlateDetail(node, params) {
      const plate = (node.plate || node.name || "").toString().toUpperCase();
      if (!plate) return;
      const pos = this.resolveClickPosition(params);
      this.detail.kind = "plate";
      this.detail.plate = node.plate || node.name;
      this.detail.detections = this.detectionsByPlate[plate] || [];
      const info = this.graphInfoByPlate[plate] || {};
      this.detail.info = info;
      this.detail.detectionsCount = info.detections_count != null
        ? info.detections_count
        : (this.detectionsByPlate[plate] || []).length || null;
      this.detail.x = pos.x;
      this.detail.y = pos.y;
      this.detail.show = true;
      this.$nextTick(this.bindDocClick);
    },
    // Vehicle node click (plate_clones report): which plate carries this
    // brand/type/color profile, so a suspicious pairing is one click away.
    showVehicleDetail(node, params) {
      const chart = this.charts[0] || {};
      const owningRel = (chart.relationships || []).find(
        (r) => r.to === node.id && String(r.from || "").startsWith("plate:")
      );
      const ownerPlate = owningRel ? String(owningRel.from).slice(6) : null;
      const pos = this.resolveClickPosition(params);

      this.detail.kind = "vehicle";
      this.detail.plate = null;
      this.detail.vehicleCaption = node.name;
      this.detail.info = {
        vehicle_caption: node.name,
        vehicle_color: node.color || null,
        owner_plate: ownerPlate,
      };
      this.detail.flows = [];
      this.detail.detectionsCount = null;
      this.detail.x = pos.x;
      this.detail.y = pos.y;
      this.detail.show = true;
      this.$nextTick(this.bindDocClick);
    },
    // Camera node click (camera_flow report): summarizes the routes that
    // pass through this camera, computed client-side from the already
    // loaded flow edges — no extra request needed.
    showCameraDetail(node, params) {
      const chart = this.charts[0] || {};
      const nodesById = {};
      (chart.nodes || []).forEach((n) => {
        nodesById[n.id] = n.caption;
      });
      const rels = (chart.relationships || []).filter((r) => r.kind !== "sequence");
      const touching = rels.filter((r) => r.from === node.id || r.to === node.id);
      const flows = touching
        .map((r) => ({
          from: nodesById[r.from] || r.from,
          to: nodesById[r.to] || r.to,
          weight: r.weight || 0,
          samplePlates: r.sample_plates || [],
        }))
        .sort((a, b) => b.weight - a.weight);

      const pos = this.resolveClickPosition(params);
      this.detail.kind = "camera";
      this.detail.cameraName = node.name;
      this.detail.plate = null;
      this.detail.info = {
        detections_count: flows.reduce((sum, f) => sum + f.weight, 0),
      };
      this.detail.flows = flows;
      this.detail.detectionsCount = null;
      this.detail.x = pos.x;
      this.detail.y = pos.y;
      this.detail.show = true;
      this.$nextTick(this.bindDocClick);
    },

    resolveClickPosition(params) {
      const fallback = { x: 10, y: 10 };
      const wrap = this.$refs.graphWrap;
      if (!wrap) return fallback;
      const rect = wrap.getBoundingClientRect();
      let clientX = null;
      let clientY = null;
      if (params.event && typeof params.event === "object") {
        clientX = params.event.clientX;
        clientY = params.event.clientY;
      }
      if (clientX == null) return fallback;
      return {
        x: Math.max(8, clientX - rect.left + 12),
        y: Math.max(8, clientY - rect.top + 12),
      };
    },
    bindDocClick() {
      if (this._docClickBound) return;
      this._docClickBound = true;
      document.addEventListener("mousedown", this.onDocClick);
    },
    unbindDocClick() {
      if (!this._docClickBound) return;
      this._docClickBound = false;
      document.removeEventListener("mousedown", this.onDocClick);
    },
    onDocClick(e) {
      const pop = this.$refs.detailPopover;
      if (pop && pop.contains(e.target)) return;
      this.closeDetail();
    },
    closeDetail() {
      this.detail.show = false;
      this.unbindDocClick();
    },
    normColor(value) {
      if (!value) return value;
      const v = String(value).toLowerCase().trim();
      return COLOR_MAP[v] ? v : value;
    },
    tagStyle(color) {
      const hex = COLOR_MAP[this.normColor(color)] || "#2E86DE";
      const style = { backgroundColor: hex, color: "#fff" };
      if (hex === "#FFFFFF" || hex === "#FDD835") {
        style.color = "#333";
        style.border = `1px solid #ccc`;
      }
      return style;
    },
    formatDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    },
    nodeTooltipHtml(node) {
      if (!node) return "";
      if (node.nodeType !== "Plate") return this.escapeHtml(node.name || "");

      const plate = (node.plate || node.name || "").toString().toUpperCase();
      const info = this.graphInfoByPlate[plate] || {};
      const t = (key) => this.$t(`grid.widgets_section.neo_dash.report.${key}`);

      const rows = [[t("plate"), plate]];
      if (info.vehicle_type) rows.push([t("type"), info.vehicle_type]);
      if (info.vehicle_color) {
        const colorLabel = this.$t(`grid.widgets_section.neo_dash.color_names.${this.normColor(info.vehicle_color)}`);
        rows.push([t("vehicle_color"), colorLabel]);
      }
      if (info.speed) rows.push([t("speed"), info.speed]);

      const rowsHtml = rows
        .map(([label, value]) => `<div><b>${this.escapeHtml(label)}:</b> ${this.escapeHtml(String(value))}</div>`)
        .join("");

      const imgHtml = info.plate_image_url
        ? `<img src="${this.escapeHtml(info.plate_image_url)}" loading="lazy" style="width:140px;max-width:100%;border-radius:4px;display:block;margin-bottom:4px;" />`
        : "";

      return `<div style="max-width:160px">${imgHtml}${rowsHtml}</div>`;
    },
    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (c) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c]);
    },
  },
};
</script>

<style lang="scss" scoped>
.neo4j-report {
  width: 100%;
  height: 100%;
  overflow-y: auto;

  &--canvas {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.neo4j-report__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.neo4j-report__heading {
  min-width: 0;
}

.neo4j-report__hint {
  margin-top: 2px;
}

.neo4j-report__state {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;
}

.neo4j-report__chart {
  width: 100%;
  height: 300px;
}

.neo4j-report__graphwrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  margin-top: 0.5rem;
  position: relative;
}

.neo4j-report__graphcard {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 0;
  padding: 0.5rem;
}

.neo4j-report__graphfill {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}

.neo4j-report__canvas {
  flex: 1 1 auto;
  width: 100%;
  min-height: 320px;
}

.neo4j-report__timeline {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0;
}

.neo4j-report__timeline-label {
  flex: 0 0 auto;
  font-size: 0.8rem;
  color: #6c757d;
  white-space: nowrap;
}

.neo4j-report__timeline-slider {
  flex: 1 1 auto;
  min-width: 0;
}

.neo4j-report__timeline-reset {
  flex: 0 0 auto;
  border: 1px solid #d8dde3;
  background: #fff;
  border-radius: 4px;
  padding: 0.1rem 0.5rem;
  font-size: 0.75rem;
  color: #495057;
  cursor: pointer;
}

.neo4j-report__chartmode {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  padding: 4px 2px;
}

.neo4j-report__kpi {
  font-size: 2.4rem;
  font-weight: 700;
  text-align: center;
  padding: 1rem 0;
}

.neo4j-report__detections {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.neo4j-report__det {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 0.5rem;
}

.neo4j-report__det-img {
  flex: 0 0 160px;
  max-width: 160px;
}

.neo4j-report__det-img .b-img-fluid,
.neo4j-report__det-img .b-img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.neo4j-report__det-noimg {
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 1.5rem 0.5rem;
  text-align: center;
}

.neo4j-report__det-info {
  flex: 1 1 auto;
  min-width: 0;
}

.neo4j-report__det-tags {
  margin-top: 0.35rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.neo4j-report__tag {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 50rem;
  background: #eef1f5;
  color: #333;
}

.neo4j-report__popover {
  position: absolute;
  z-index: 20;
  width: 300px;
  max-width: calc(100% - 16px);
  max-height: 70%;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d8dde3;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  font-size: 0.85rem;
}

.neo4j-report__popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #eee;
  background: #f7f8fa;
  border-radius: 8px 8px 0 0;
}

.neo4j-report__popover-close {
  border: none;
  background: transparent;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  color: #666;
}

.neo4j-report__popover-body {
  padding: 0.5rem 0.6rem;
}

.neo4j-report__scene-img {
  margin-bottom: 0.5rem;
}

.neo4j-report__scene-img .b-img-fluid,
.neo4j-report__scene-img .b-img {
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.neo4j-report__plate-img {
  margin-bottom: 0.5rem;
}

.neo4j-report__plate-img .b-img-fluid,
.neo4j-report__plate-img .b-img {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.neo4j-report__fields {
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.15rem 0.5rem;
  font-size: 0.8rem;
}

.neo4j-report__field {
  display: contents;
}

.neo4j-report__field dt {
  color: #6c757d;
  font-weight: 500;
}

.neo4j-report__field dd {
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.neo4j-report__clone-alert {
  margin-bottom: 0.5rem;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
  background: #fdecea;
  color: #c0392b;
  font-weight: 600;
  font-size: 0.8rem;
}

.neo4j-report__flows {
  margin-top: 0.4rem;
}

.neo4j-report__flow-row {
  padding: 0.2rem 0;
  border-top: 1px solid #eee;
}

.neo4j-report__swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
</style>
